import {
  Component,
  Input,
  output,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
  signal,
  computed,
  input,
  OnInit,
} from '@angular/core';
import { UserProfilePictureComponent } from '../user-profile-picture/user-profile-picture.component';
import { AsyncPipe } from '@angular/common';
import { NickyButtonComponent } from '../form/nicky-button/nicky-button.component';
import { BehaviorSubject, Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { NickSearchResult } from '@app/services/search.service';
import { openLinkInANewTab } from '@app/utils/open-in-a-new-tab.utils';
import { User } from '../models/user.mode';
import { PayerInfoTooltipComponent } from '../payer-info-tooltip/payer-info-tooltip.component';
import { ModalsService } from '@app/services/modals.service';
import { PublicProfileComponent } from '@app/pages/public/public-profile/public-profile.component';
import { ProfileDto } from '@app/dtos/user-profile.dto';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SearchService } from '@app/services/search.service';
import GetSanitizedImageUrlUtil from '@app/utils/get-sanitized-image-url-util';
import { VALIDATION_PATTERNS } from '@app/utils/validation-patterns.util';
import { TranslateModule } from '@ngx-translate/core';
import { ContactsAutocompleteComponent } from '../contacts-autocomplete/contacts-autocomplete.component';
import { Contact } from '../models/contact.model';

@Component({
  selector: 'app-nicky-user-guest-card',
  imports: [
    UserProfilePictureComponent,
    NickyButtonComponent,
    AsyncPipe,
    PayerInfoTooltipComponent,
    ReactiveFormsModule,
    TranslateModule,
    ContactsAutocompleteComponent,
  ],
  templateUrl: './nicky-user-guest-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NickyUserGuestCardComponent implements OnDestroy, OnInit {
  private modalService = inject(ModalsService);
  private searchService = inject(SearchService);
  private getSanitizedImageUrl = inject(GetSanitizedImageUrlUtil);
  private fb = inject(FormBuilder);

  @Input() enableSearchInput: boolean = false;
  userSelected = output<ProfileDto | undefined>();
  contactSelected = output<Contact>();

  searchForm: FormGroup = this.fb.group({
    searchTerm: [''],
  });
  isSearching = false;

  searchTermSignal = signal<string>('');

  @Input() userEmail: string | null | undefined;
  @Input() block: boolean = false;
  @Input() hideClearButton = false;
  @Input() noPayerInfoTooltip = false;
  @Input() payerName = '';
  @Input() payerEmail = '';
  @Input() targetUser: NickSearchResult | User | ProfileDto | undefined = undefined;
  @Input() inputPlaceholder: string = 'DASHBOARD.startBySearchingAnEmail';
  @Input() set isLoading(value: boolean) {
    this._isLoading.next(value);
  }

  editInfo = output<void>();
  onClearUser = output<void>();

  private _isLoading = new BehaviorSubject<boolean>(false);
  private destroy$ = new Subject<void>();

  protected showTargetInfo = false;
  protected isLoadingDebounced$ = this._isLoading.pipe(debounceTime(700), takeUntil(this.destroy$));

  get isLoading(): boolean {
    return this._isLoading.value;
  }
  openPublicProfile(shortId: string) {
    openLinkInANewTab(`${window.location.origin}/s/${shortId}`);
  }

  clickHandler() {
    if (this.noPayerInfoTooltip || this.targetUser?.shortId) {
      this.modalService.openModal(PublicProfileComponent, { data: this.targetUser, autoFocus: 'cdkFocusInitial' });
      return;
    }
    this.showTargetInfo = true;
  }

  private setupSearchInput() {
    this.searchForm
      .get('searchTerm')
      ?.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(async (email: string) => {
        this.searchTermSignal.set(email ?? '');
        if (email && VALIDATION_PATTERNS.EMAIL.test(email)) {
          this.isSearching = true;
          try {
            const user = await this.searchService.getPublicProfileByEmail(email.trim()).toPromise();
            if (user) {
              user.profileImageUrl = await this.getSanitizedImageUrl.getSanizedImageUrlByUserId(user.id || '');
              this.userSelected.emit(user);
              this.isSearching = false;
              return;
            }
            this.userSelected.emit(undefined);
          } catch (e) {
            this.userSelected.emit(undefined);
          } finally {
            this.isSearching = false;
          }
        } else {
          this.userSelected.emit(undefined);
        }
      });
  }

  onSelectContact(contact: Contact) {
    this.searchForm.patchValue({ searchTerm: contact.email || contact.nick });
    this.contactSelected.emit(contact);
    this.userSelected.emit({
      email: contact.email || contact.nick,
      publicName: contact.publicName || contact.name || '',
    } as ProfileDto);
  }

  ngOnInit() {
    if (this.enableSearchInput) {
      this.setupSearchInput();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
